/**
 * "Skip this welcome screen next time" — the preference, and where it sends you.
 *
 * SPN's own, modelled on `apr/skipStart.ts`: products share no code between their folders
 * (CLAUDE.md §1.7), and the keys must not be shared either — skipping APR's welcome screen
 * should say nothing about whether you want SPN's.
 *
 * Two keys rather than one, because they answer different questions: whether to skip at
 * all, and what to open instead. Kept in their own module because the start page WRITES
 * them and the module router READS them, and a second copy of a key name is how the two
 * quietly stop agreeing.
 *
 * A flag with no destination is not skipping — it is arriving nowhere. APR shipped exactly
 * that once: a toggle that saved a preference nothing consulted.
 */

const SKIP_KEY = 'spn.skipStart';
const TARGET_KEY = 'spn.skipStartTarget';

/**
 * Where skipping can land you. BOTH ARE REAL SCREENS, not buckets.
 *
 * SPN has only these two destinations, and neither is a menu of cards, so unlike APR there
 * is nothing to resolve at navigation time — the stored path is the screen.
 */
export const SKIP_TARGETS = [
  { path: '/spn/discoveries', label: 'Discoveries' },
  { path: '/spn/transfer', label: 'Transfer' },
] as const;

export const DEFAULT_SKIP_TARGET = SKIP_TARGETS[0].path;

export function isSkipOn(): boolean {
  return localStorage.getItem(SKIP_KEY) === '1';
}

export function getSkipTarget(): string {
  const stored = localStorage.getItem(TARGET_KEY);
  // Only a target still on the list is honoured. A path saved before the list changed
  // would otherwise send someone to a screen that no longer exists.
  return SKIP_TARGETS.some((t) => t.path === stored) ? stored! : DEFAULT_SKIP_TARGET;
}

export function setSkip(on: boolean): void {
  if (on) localStorage.setItem(SKIP_KEY, '1');
  else localStorage.removeItem(SKIP_KEY);
}

export function setSkipTarget(path: string): void {
  localStorage.setItem(TARGET_KEY, path);
}

/** Clears both keys — the welcome screen comes back next time. */
export function resetSkip(): void {
  localStorage.removeItem(SKIP_KEY);
  localStorage.removeItem(TARGET_KEY);
}

/**
 * Where entering the module should land, or null to show the welcome screen.
 *
 * `?start=1` ALWAYS wins. Without an escape hatch, turning the skip on would make the
 * welcome screen — and the toggle that turns it back off — permanently unreachable.
 */
export function shouldSkipStart(search: string): string | null {
  if (new URLSearchParams(search).get('start') === '1') return null;
  return isSkipOn() ? getSkipTarget() : null;
}
